import { $themeBreakpoints } from '@themeConfig';
import useService from '@/auth/service/useService';
import valuableCurrencies from '@/constants/currencies.json';
import i18n from '@/libs/i18n';

const statesCache = {};
const citiesCache = {};

export default {
  namespaced: true,
  state: {
    windowWidth: 0,
    shallShowOverlay: false,
    countries: [],
    currencies: [],
    consignorStates: [],
    consignorCities: [],
    consigneeStates: [],
    consigneeCities: [],
    preferences: [],
    exportProgress: 0,
    exportFile: '',
    importProgress: 0,
    importFile: '',
    checkedProvider: null,
    checkedPermissions: [],
    checkedRoles: [],
    allProviders: [],
    consignmentTypes: [],
    allRoles: [],
    allPermissions: [],
    appReady: false,
    returnItems: [],
    appliedFilters: null,
    regionModelType: [],
    providerDetails: {},
    pickupStatus: [],
    pickupConsignmentStatus: []
  },
  getters: {
    currentBreakPoint: state => {
      const { windowWidth } = state;
      if (windowWidth >= $themeBreakpoints.xl) return 'xl';
      if (windowWidth >= $themeBreakpoints.lg) return 'lg';
      if (windowWidth >= $themeBreakpoints.md) return 'md';
      if (windowWidth >= $themeBreakpoints.sm) return 'sm';
      return 'xs';
    },
    countries: state => state.countries,
    currencies: state => state.currencies,
    consignorStates: state => state.consignorStates,
    consignorCities: state => state.consignorCities,
    consigneeStates: state => state.consigneeStates,
    consigneeCities: state => state.consigneeCities,
    preferences: state => state.preferences,
    exportProgress: state => state.exportProgress,
    exportFile: state => state.exportFile,
    importProgress: state => state.importProgress,
    importFile: state => state.importFile,
    checkedProvider: state => state.checkedProvider,
    checkedPermissions: state => state.checkedPermissions,
    checkedRoles: state => state.checkedRoles,
    allProviders: state => state.allProviders,
    allPermissions: state => state.allPermissions,
    consignmentTypes: state => state.consignmentTypes,
    allRoles: state => state.allRoles,
    returnItems: state => state.returnItems,
    regionModelType: state => state.regionModelType,
    providerDetails: state => state.providerDetails,
    pickupStatus: state => state.pickupStatus,
    pickupConsignmentStatus: state => state.pickupConsignmentStatus
  },
  mutations: {
    UPDATE_WINDOW_WIDTH(state, val) {
      state.windowWidth = val;
    },
    TOGGLE_OVERLAY(state, val) {
      state.shallShowOverlay = val !== undefined ? val : !state.shallShowOverlay;
    },
    SET_COUNTRIES(state, val) {
      state.countries = val;
    },
    SET_CURRENCIES(state, val) {
      state.currencies = val;
    },
    SET_CONSIGNOR_STATES(state, val) {
      state.consignorStates = val;
    },
    SET_CONSIGNOR_CITIES(state, val) {
      state.consignorCities = val;
    },
    SET_CONSIGNEE_STATES(state, val) {
      state.consigneeStates = val;
    },
    SET_CONSIGNEE_CITIES(state, val) {
      state.consigneeCities = val;
    },
    SET_PREFERENCES(state, val) {
      state.preferences = val;
    },
    SET_EXPORT_PROGRESS(state, val) {
      state.exportProgress = val;
    },
    SET_EXPORT_FILE(state, val) {
      state.exportFile = val;
    },
    SET_IMPORT_PROGRESS(state, val) {
      state.importProgress = val;
    },
    SET_IMPORT_FILE(state, val) {
      state.importFile = val;
    },
    SET_CHECKED_PROVIDER(state, val) {
      state.checkedProvider = val;
    },
    SET_ALL_PROVIDERS(state, val) {
      state.allProviders = val;
    },
    SET_ALL_PERMISSIONS(state, val) {
      state.allPermissions = val;
    },
    SET_ALL_ROLES(state, val) {
      state.allRoles = val;
    },
    SET_CONSIGNMENT_TYPES(state, types) {
      state.consignmentTypes = types;
    },
    SET_APP_READY(state, val) {
      state.appReady = val;
    },
    SET_RETURN_ITEMS(state, val) {
      state.returnItems = val;
    },
    SET_APPLIED_FILTERS(state, val) {
      if (state.appliedFilters) {
        state.appliedFilters = { ...state.appliedFilters, ...val };
      } else {
        state.appliedFilters = { ...val };
      }
    },
    SET_REGION_MODEL_TYPE(state, val) {
      state.regionModelType = val;
    },
    SET_PROVIDER_DETAILS(state, val) {
      state.providerDetails = val;
    },
    SET_PICKUP_STATUS(state, val) {
      state.pickupStatus = val;
    },
    SET_PICKUP_CONSIGNMENT_STATUS(state, val) {
      state.pickupConsignmentStatus = val;
    }
  },
  actions: {
    async getCountries({ commit }) {
      return useService
        .getCountryList()
        .then(async response => {
          await commit(
            'SET_COUNTRIES',
            response.data.map(item => ({
              text: item.name,
              value: item.id,
              iso3: item.iso3,
              iso2: item.iso2,
              isPostalCodeRequired: item.isPostalCodeRequired
            }))
          );
        })
        .catch(error => Promise.reject(error));
    },
    async getCurrencies({ commit }) {
      await commit(
        'SET_CURRENCIES',
        valuableCurrencies?.map(item => ({
          text: `${i18n.t(item.name)} (${item.code_text})`,
          value: item.id,
          code: item.code,
          codeText: item.code_text,
          symbol: item.symbol
        }))
      );
    },
    async getConsignorStates({ commit }, countryId) {
      await commit('SET_CONSIGNOR_STATES', []);
      if (!statesCache[countryId]) {
        statesCache[countryId] = useService
          .getStateList(countryId)
          .then(response => response.data)
          .catch(error => {
            delete statesCache[countryId];
            throw error;
          });
      }
      try {
        const data = await statesCache[countryId];
        await commit(
          'SET_CONSIGNOR_STATES',
          data.map(item => ({
            text: item.name,
            value: item.id,
            stateCode: item.stateCode
          }))
        );
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async getConsignorCities({ commit }, params) {
      await commit('SET_CONSIGNOR_CITIES', []);
      const key = `${params.countryId}-${params.cityId}`;
      if (!citiesCache[key]) {
        citiesCache[key] = useService
          .getCityList(params.countryId, params.cityId)
          .then(response => response.data)
          .catch(error => {
            delete citiesCache[key];
            throw error;
          });
      }
      try {
        const data = await citiesCache[key];
        await commit(
          'SET_CONSIGNOR_CITIES',
          data.map(item => ({
            text: item.name,
            value: item.id
          }))
        );
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async getConsigneeStates({ commit }, countryId) {
      await commit('SET_CONSIGNEE_STATES', []);
      if (!statesCache[countryId]) {
        statesCache[countryId] = useService
          .getStateList(countryId)
          .then(response => response.data)
          .catch(error => {
            delete statesCache[countryId];
            throw error;
          });
      }
      try {
        const data = await statesCache[countryId];
        await commit(
          'SET_CONSIGNEE_STATES',
          data.map(item => ({
            text: item.name,
            value: item.id
          }))
        );
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async getConsigneeCities({ commit }, params) {
      await commit('SET_CONSIGNEE_CITIES', []);
      const key = `${params.countryId}-${params.cityId}`;
      if (!citiesCache[key]) {
        citiesCache[key] = useService
          .getCityList(params.countryId, params.cityId)
          .then(response => response.data)
          .catch(error => {
            delete citiesCache[key];
            throw error;
          });
      }
      try {
        const data = await citiesCache[key];
        await commit(
          'SET_CONSIGNEE_CITIES',
          data.map(item => ({
            text: item.name,
            value: item.id
          }))
        );
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async getPreferences({ commit }) {
      return useService
        .getPreferences()
        .then(async response => {
          await commit(
            'SET_PREFERENCES',
            response.data.map(item => ({
              text: item.info,
              value: item.value,
              key: item.key
            }))
          );
        })
        .catch(error => Promise.reject(error));
    },
    async getConsignmentType({ commit }) {
      return useService
        .getConsignmentType()
        .then(async response => {
          await commit(
            'SET_CONSIGNMENT_TYPES',
            response.data.map(item => ({
              text: item.info,
              value: item.value,
              key: item.key
            }))
          );
        })
        .catch(error => Promise.reject(error));
    },
    async getRegionModelType({ commit }) {
      return useService
        .getRegionModelType()
        .then(async response => {
          await commit(
            'SET_REGION_MODEL_TYPE',
            response.data.map(item => ({
              text: item.info,
              value: item.value,
              key: item.key
            }))
          );
        })
        .catch(error => Promise.reject(error));
    },
    async getProviderDetails({ commit }, id) {
      return useService
        .getProvider(id)
        .then(async response => {
          await commit('SET_PROVIDER_DETAILS', response.data);
        })
        .catch(error => Promise.reject(error));
    },
    async getPickupStatusLabels({ commit }) {
      return useService
        .getPickupStatus()
        .then(async response => {
          await commit(
            'SET_PICKUP_STATUS',
            response.data.map(item => ({
              text: item.info,
              value: item.value,
              key: item.key
            }))
          );
        })
        .catch(error => Promise.reject(error));
    },
    async getPickupConsignmentStatusLabels({ commit }) {
      return useService
        .getPickupConsignmentStatus()
        .then(async response => {
          await commit(
            'SET_PICKUP_CONSIGNMENT_STATUS',
            response.data.map(item => ({
              text: item.info,
              value: item.value,
              key: item.key
            }))
          );
        })
        .catch(error => Promise.reject(error));
    },

    setAppliedFilters({ commit }, payload) {
      commit('SET_APPLIED_FILTERS', payload);
    }
  }
};
